import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Chip, 
  IconButton, 
  Tooltip,
  Checkbox,
  FormControlLabel,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Avatar
} from '@mui/material';
import { 
  RocketLaunch as RocketIcon, 
  Public as GlobalIcon, 
  Add as AddIcon, 
  Edit as EditIcon, 
  Close as CloseIcon,
  CheckCircle as ApprovedIcon,
  Warning as PendingIcon,
  Block as BlockedIcon
} from '@mui/icons-material';

// Mock data generators
const generateMockReleases = () => {
  return [
    {
      id: 'release-2024-q2-01',
      name: 'Q2 2024 Platform Update',
      version: 'v2.4.5',
      targetDate: '2024-06-15',
      status: 'Pending',
      stories: [
        {
          id: 'story-001',
          title: 'Enhanced Security Features',
          type: 'Feature',
          rolloutStatus: 'Pending',
          countries: []
        },
        {
          id: 'story-002',
          title: 'Performance Optimization',
          type: 'Enhancement',
          rolloutStatus: 'Pending',
          countries: []
        },
        {
          id: 'story-003',
          title: 'Bug Fix: Login Timeout',
          type: 'Defect',
          rolloutStatus: 'Pending',
          countries: []
        }
      ]
    },
    {
      id: 'release-2024-q3-01',
      name: 'Q3 2024 AI Integration',
      version: 'v2.5.0',
      targetDate: '2024-09-01',
      status: 'Planning',
      stories: [
        {
          id: 'story-004',
          title: 'AI-Powered Analytics',
          type: 'Feature',
          rolloutStatus: 'Planning',
          countries: []
        },
        {
          id: 'story-005',
          title: 'Machine Learning Dashboard',
          type: 'Feature',
          rolloutStatus: 'Planning',
          countries: []
        }
      ]
    }
  ];
};

const mockCountries = [
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬' }
];

const GlobalRollouts = ({collapsed}) => {
  const [releases, setReleases] = useState(generateMockReleases());
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [selectedStory, setSelectedStory] = useState(null);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [isRolloutDialogOpen, setIsRolloutDialogOpen] = useState(false);

  // Handle selecting a release for rollout configuration
  const handleSelectRelease = (release) => {
    setSelectedRelease(release);
    setSelectedStory(null);
    setSelectedCountries([]);
  };

  // Open rollout configuration dialog
  const handleOpenRolloutDialog = (story) => {
    setSelectedStory(story);
    setSelectedCountries(story.countries || []);
    setIsRolloutDialogOpen(true);
  };

  // Handle country selection toggle
  const handleCountryToggle = (countryCode) => {
    setSelectedCountries(prev => 
      prev.includes(countryCode)
        ? prev.filter(code => code !== countryCode)
        : [...prev, countryCode]
    );
  };

  // Save rollout configuration
  const handleSaveRolloutConfig = () => {
    // Update the selected story's countries
    const updatedReleases = releases.map(release => {
      if (release.id === selectedRelease.id) {
        return {
          ...release,
          stories: release.stories.map(story => 
            story.id === selectedStory.id 
              ? { ...story, countries: selectedCountries } 
              : story
          )
        };
      }
      return release;
    });

    setReleases(updatedReleases);
    setIsRolloutDialogOpen(false);
    setSelectedStory(null);
  };

  // Determine story rollout status
  const getStoryRolloutStatus = (story) => {
    if (story.countries.length === 0) return 'Pending';
    if (story.countries.length === mockCountries.length) return 'Global';
    return 'Partial';
  };

  // Get status color
  const getStatusColor = (status) => {
    switch(status) {
      case 'Pending': return '#F59E0B';
      case 'Global': return '#10B981';
      case 'Partial': return '#3B82F6';
      default: return '#6B7280';
    }
  };

  // Render release card
  const renderReleaseCard = (release) => (
    <Card 
      key={release.id} 
      sx={{ 
        bgcolor: 'rgba(16, 24, 40, 0.8)', 
        border: '1px solid rgba(59, 130, 246, 0.2)',
        mb: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)'
        }
      }}
    >
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'rgba(59, 130, 246, 0.2)', color: '#3B82F6' }}>
            <RocketIcon />
          </Avatar>
        }
        title={
          <Typography 
            variant="h6" 
            color="white" 
            sx={{ fontWeight: 'bold' }}
          >
            {release.name}
          </Typography>
        }
        subheader={
          <Typography 
            variant="body2" 
            color="rgba(255,255,255,0.7)"
          >
            Version {release.version} | Target: {release.targetDate}
          </Typography>
        }
        action={
          <Button 
            variant="outlined"
            color="primary"
            onClick={() => handleSelectRelease(release)}
            sx={{ 
              color: '#3B82F6', 
              borderColor: 'rgba(59, 130, 246, 0.5)',
              '&:hover': {
                bgcolor: 'rgba(59, 130, 246, 0.1)'
              }
            }}
          >
            Configure Rollout
          </Button>
        }
      />
      <Divider sx={{ bgcolor: 'rgba(59, 130, 246, 0.2)' }} />
      <CardContent>
        <Grid container spacing={2}>
          {release.stories.map((story) => (
            <Grid item xs={12} key={story.id}>
              <Paper 
                elevation={0} 
                sx={{ 
                  bgcolor: 'rgba(13, 26, 48, 0.5)', 
                  p: 2, 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: '1px solid rgba(59, 130, 246, 0.1)'
                }}
              >
                <Box>
                  <Typography 
                    variant="body1" 
                    color="white" 
                    sx={{ fontWeight: 'medium' }}
                  >
                    {story.title}
                  </Typography>
                  <Chip 
                    label={story.type} 
                    size="small" 
                    sx={{ 
                      mt: 1,
                      bgcolor: story.type === 'Feature' 
                        ? 'rgba(59, 130, 246, 0.2)' 
                        : story.type === 'Enhancement'
                        ? 'rgba(16, 185, 129, 0.2)'
                        : 'rgba(239, 68, 68, 0.2)',
                      color: story.type === 'Feature' 
                        ? '#3B82F6' 
                        : story.type === 'Enhancement'
                        ? '#10B981'
                        : '#EF4444'
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip 
                    icon={
                      getStoryRolloutStatus(story) === 'Pending' ? <PendingIcon /> :
                      getStoryRolloutStatus(story) === 'Global' ? <ApprovedIcon /> :
                      <BlockedIcon />
                    }
                    label={getStoryRolloutStatus(story)}
                    size="medium"
                    sx={{ 
                      bgcolor: `${getStatusColor(getStoryRolloutStatus(story))}20`, 
                      color: getStatusColor(getStoryRolloutStatus(story)),
                      fontWeight: 'bold'
                    }}
                  />
                  <Tooltip title="Configure Rollout">
                    <IconButton 
                      onClick={() => handleOpenRolloutDialog(story)}
                      sx={{ 
                        color: '#3B82F6',
                        bgcolor: 'rgba(59, 130, 246, 0.1)',
                        '&:hover': {
                          bgcolor: 'rgba(59, 130, 246, 0.2)'
                        }
                      }}
                    >
                      <GlobalIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box 
    sx={{ 
      bgcolor: '#050a18', 
      // minHeight: '100vh', 
      p: { xs: 2, sm: 3, md: 4 }, 
      color: 'white',
      marginLeft: collapsed ? "60px" : "240px", // Add dynamic margin based on sidebar state
      transition: 'margin-left 0.3s ease', // Add smooth transition for sidebar toggle
      '@media (max-width: 1200px)': {
        marginLeft: collapsed ? "60px" : "240px",
      },
      '@media (max-width: 900px)': {
        marginLeft: collapsed ? "60px" : "180px", // Smaller sidebar on medium screens
      },
      '@media (max-width: 600px)': {
        marginLeft: collapsed ? "60px" : "60px", // On mobile, sidebar is likely collapsed
        p: { xs: 1, sm: 2 }, // Smaller padding on mobile
      }
    }}
    >
      <Typography 
        variant="h4" 
        sx={{ 
          mb: 4, 
          fontWeight: 'bold', 
          textAlign: 'center',
          color: '#3B82F6'
        }}
      >
        Global Release Rollouts
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={selectedRelease ? 6 : 12}>
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                color: '#3B82F6'
              }}
            >
              <RocketIcon /> Upcoming Releases
            </Typography>
            {releases.map(renderReleaseCard)}
          </Box>
        </Grid>

        {selectedRelease && (
          <Grid item xs={12} md={6}>
            <Box 
              sx={{ 
                bgcolor: 'rgba(16, 24, 40, 0.8)', 
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: 2,
                p: 3
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" color="#3B82F6">
                  {selectedRelease.name} Rollout Configuration
                </Typography>
                <IconButton onClick={() => setSelectedRelease(null)}>
                  <CloseIcon sx={{ color: 'white' }} />
                </IconButton>
              </Box>
              
              <Typography variant="body1" sx={{ mb: 2, color: 'rgba(255,255,255,0.7)' }}>
                Select a story to configure its global rollout
              </Typography>

              <Grid container spacing={2}>
                {selectedRelease.stories.map((story) => (
                  <Grid item xs={12} key={story.id}>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        bgcolor: 'rgba(13, 26, 48, 0.5)', 
                        p: 2, 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        border: '1px solid rgba(59, 130, 246, 0.1)'
                      }}
                    >
                      <Typography 
                        variant="body1" 
                        color="white" 
                        sx={{ fontWeight: 'medium' }}
                      >
                        {story.title}
                      </Typography>
                      <Button 
                        variant="outlined"
                        startIcon={<GlobalIcon />}
                        onClick={() => handleOpenRolloutDialog(story)}
                        sx={{ 
                          color: '#3B82F6', 
                          borderColor: 'rgba(59, 130, 246, 0.5)',
                          '&:hover': {
                            bgcolor: 'rgba(59, 130, 246, 0.1)'
                          }
                        }}
                      >
                        Configure Countries
                      </Button>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Country Rollout Configuration Dialog */}
      <Dialog 
        open={isRolloutDialogOpen}
        onClose={() => setIsRolloutDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: '#0c1629',
            color: 'white',
            borderRadius: '12px',
            border: '1px solid rgba(59, 130, 246, 0.2)'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'rgba(16, 24, 40, 0.8)', 
          color: '#3B82F6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          ConfigureRollout for {selectedStory?.title}
          <IconButton 
            onClick={() => setIsRolloutDialogOpen(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          bgcolor: '#0c1629', 
          p: 3 
        }}>
          <Typography variant="subtitle1" sx={{ mb: 2, color: 'rgba(255,255,255,0.7)' }}>
            Select countries where this {selectedStory?.type.toLowerCase()} will be rolled out
          </Typography>
          
          <Grid container spacing={2}>
            {mockCountries.map((country) => (
              <Grid item xs={12} sm={6} md={4} key={country.code}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCountries.includes(country.code)}
                      onChange={() => handleCountryToggle(country.code)}
                      sx={{
                        color: 'rgba(59, 130, 246, 0.5)',
                        '&.Mui-checked': {
                          color: '#3B82F6',
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: '1.2rem', mr: 1 }}>
                        {country.flag}
                      </Typography>
                      <Typography color="white">
                        {country.name}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    bgcolor: selectedCountries.includes(country.code) 
                      ? 'rgba(59, 130, 246, 0.1)' 
                      : 'transparent',
                    borderRadius: 1,
                    px: 1,
                    width: '100%',
                    margin: 0,
                    border: selectedCountries.includes(country.code)
                      ? '1px solid rgba(59, 130, 246, 0.3)'
                      : '1px solid transparent'
                  }}
                />
              </Grid>
            ))}
          </Grid>

          {/* Quick Selection Options */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: 2, 
            mt: 3,
            mb: 2
          }}>
            <Button
              variant="outlined"
              onClick={() => setSelectedCountries(mockCountries.map(c => c.code))}
              sx={{ 
                color: '#3B82F6', 
                borderColor: 'rgba(59, 130, 246, 0.5)',
                '&:hover': {
                  bgcolor: 'rgba(59, 130, 246, 0.1)'
                }
              }}
            >
              Select All
            </Button>
            <Button
              variant="outlined"
              onClick={() => setSelectedCountries([])}
              sx={{ 
                color: '#EF4444', 
                borderColor: 'rgba(239, 68, 68, 0.5)',
                '&:hover': {
                  bgcolor: 'rgba(239, 68, 68, 0.1)'
                }
              }}
            >
              Clear Selection
            </Button>
          </Box>

          {/* Selection Summary */}
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            bgcolor: 'rgba(16, 24, 40, 0.5)',
            borderRadius: 2,
            border: '1px solid rgba(59, 130, 246, 0.2)'
          }}>
            <Typography variant="body2" color="rgba(255,255,255,0.7)">
              Selected Countries: {selectedCountries.length} / {mockCountries.length}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1, 
              mt: 1 
            }}>
              {selectedCountries.map(code => {
                const country = mockCountries.find(c => c.code === code);
                return (
                  <Chip
                    key={code}
                    label={`${country.flag} ${country.name}`}
                    onDelete={() => handleCountryToggle(code)}
                    sx={{
                      bgcolor: 'rgba(59, 130, 246, 0.2)',
                      color: '#3B82F6'
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          bgcolor: 'rgba(16, 24, 40, 0.8)', 
          p: 2,
          justifyContent: 'space-between'
        }}>
          <Button
            onClick={() => setIsRolloutDialogOpen(false)}
            sx={{ 
              color: 'rgba(255,255,255,0.7)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveRolloutConfig}
            disabled={selectedCountries.length === 0}
            sx={{ 
              bgcolor: '#3B82F6',
              '&:hover': {
                bgcolor: '#2563EB'
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(59, 130, 246, 0.3)',
                color: 'rgba(255,255,255,0.5)'
              }
            }}
          >
            Save Rollout Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GlobalRollouts;